import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import {
	Card,
	Table,
	Stack,
	Paper,
	Button,
	Popover,
	TableRow,
	LinearProgress,
	MenuItem,
	TableBody,
	TableCell,
	Container,
	Typography,
	IconButton,
	TableContainer,
	TablePagination,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import { ListHead, ListToolbar, type HeadCell } from '../../components/list';

import { useGetEmailTemplatesQuery, useDelEmailTemplateMutation } from '../../slices/apis/emailTemplateApi';
import { applySortFilter, getComparator, type Order } from '../../utils/tableSorter';
import type { EmailTemplateProps } from '../../models';

// ----------------------------------------------------------------------

const TABLE_HEAD: HeadCell[] = [
	{ id: 'id', label: 'ID', align: 'left' },
	{ id: 'name', label: 'Name', align: 'left' },
	{ id: 'subject', label: 'Subject', align: 'left' },
	{ id: '', label: '', align: 'right' },
];

// ----------------------------------------------------------------------

export default function EmailTemplates() {
	const [menuOpen, setMenuOpen] = useState<HTMLElement | null>(null);
	const [page, setPage] = useState<number>(0);
	const [order, setOrder] = useState<Order>('asc');
	const [orderBy, setOrderBy] = useState<keyof EmailTemplateProps>('name');
	const [selected] = useState<string[]>([]);
	const [filterName, setFilterName] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | number | null>(null);
	const navigate = useNavigate();
	const { data, isLoading } = useGetEmailTemplatesQuery();
	const [delEmailTemplate] = useDelEmailTemplateMutation();

	let emptyRows = 0;

	let filteredData: EmailTemplateProps[] = [];
	let isNotFound = !filteredData.length && !!filterName;

	const handleOpenMenu = useCallback((e: MouseEvent<HTMLElement>) => {
		setMenuOpen(e.currentTarget);
		setSelectedTemplateId(e.currentTarget.getAttribute('data-email-template-id'));
	}, []);

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDataSort = (event: MouseEvent<unknown>, property: keyof EmailTemplateProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleDeleteTemplate = () => {
		delEmailTemplate(selectedTemplateId);
		setMenuOpen(null);
	};

	const handleEditTemplate = () => {
		navigate(`/dashboard/admin/email-templates/${selectedTemplateId}/edit`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Email Templates</title>
				</Helmet>
				<Container sx={{ width: '75%' }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
						<Typography variant="h4" gutterBottom>
							Email Templates
						</Typography>
					</Stack>
					<LinearProgress />
				</Container>
			</>
		);
	}

	if (data) {
		emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
		filteredData = applySortFilter({
			list: data,
			comparator: getComparator(order, orderBy),
			query: filterName,
			orderBy: 'name',
		});
		isNotFound = !filteredData.length && !!filterName;
	}

	return (
		<>
			<Helmet>
				<title>Email Templates - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container sx={{ width: '75%' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="landing_page_heading" gutterBottom>
						Email Templates
					</Typography>
					<Button
						variant="contained"
						data-route="/dashboard/admin/email-templates/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Template
					</Button>
				</Stack>

				<Card>
					<ListToolbar
						numSelected={selected.length}
						filterName={filterName}
						onFilterName={handleFilterByName}
						placeHolderText={'Template name...'}
					/>

					<Scrollbar>
						<TableContainer>
							<Table>
								<ListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleDataSort} />
								<TableBody>
									{filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
										const { id, name, subject } = row;

										return (
											<TableRow hover key={id} tabIndex={-1} selected={selected.indexOf(name) !== -1}>
												<TableCell align="left">
													<Typography variant="subtitle2">{id}</Typography>
												</TableCell>
												<TableCell align="left">{name}</TableCell>
												<TableCell align="left">{subject}</TableCell>
												<TableCell align="right">
													<IconButton size="large" color="inherit" data-email-template-id={id} onClick={handleOpenMenu}>
														<Iconify icon={'eva:more-vertical-fill'} />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
									{emptyRows > 0 && (
										<TableRow style={{ height: 53 * emptyRows }}>
											<TableCell colSpan={6} />
										</TableRow>
									)}
								</TableBody>

								{isNotFound && (
									<TableBody>
										<TableRow>
											<TableCell align="center" colSpan={6} sx={{ py: 3 }}>
												<Paper
													sx={{
														textAlign: 'center',
													}}
												>
													<Typography variant="h6" paragraph>
														Not found
													</Typography>

													<Typography variant="body2">
														No results found for &nbsp;
														<strong>&quot;{filterName}&quot;</strong>.
														<br /> Check your typos or use complete words
													</Typography>
												</Paper>
											</TableCell>
										</TableRow>
									</TableBody>
								)}
							</Table>
						</TableContainer>
					</Scrollbar>

					<TablePagination
						rowsPerPageOptions={[5, 10, 25]}
						component="div"
						count={data ? data.length : 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Card>
			</Container>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEditTemplate}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					View/Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteTemplate}>
					<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
